import { NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, MagicWand, CalendarBlank, PenNib, CreditCard,
  Sun, Moon, SignOut, CaretDown, Plus, Lightning, PlugsConnected, Image as ImageIcon, FolderSimple, ChartLineUp,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import PastDueBanner from "@/components/PastDueBanner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/studio", label: "New Repurpose", icon: MagicWand },
  { to: "/library", label: "Content Library", icon: PenNib },
  { to: "/visuals", label: "AI Visuals", icon: ImageIcon },
  { to: "/media", label: "Media Library", icon: FolderSimple },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank },
  { to: "/brand-voice", label: "Brand Voice", icon: Lightning },
  { to: "/connections", label: "Connections", icon: PlugsConnected },
  { to: "/billing", label: "Billing & Plan", icon: CreditCard },
];

const ADMIN_NAV = [
  { to: "/insights", label: "Insights", icon: ChartLineUp },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { workspaces, active, setActiveId, refresh } = useWorkspace();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const usagePct = active ? Math.min(100, Math.round((active.usage_count / active.usage_limit) * 100)) : 0;

  const addWorkspace = async () => {
    const name = window.prompt("New brand / workspace name:");
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/workspaces", { name });
      await refresh();
      setActiveId(data.id);
      toast.success("Workspace created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create workspace");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background grain">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/40 fixed h-full z-20">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <MagicWand size={18} weight="fill" className="text-primary-foreground" />
            </div>
            <span className="font-heading font-black text-xl tracking-tight">PostPilot</span>
          </div>
        </div>

        {/* Workspace switcher */}
        <div className="p-4 border-b border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="workspace-switcher"
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md border border-border bg-background hover:bg-accent transition-colors"
              >
                <div className="text-left overflow-hidden">
                  <p className="text-sm font-medium truncate">{active?.name || "Workspace"}</p>
                  <p className="text-xs text-muted-foreground font-mono uppercase">{active?.plan || "trial"}</p>
                </div>
                <CaretDown size={14} className="text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start">
              {workspaces.map((w) => (
                <DropdownMenuItem
                  key={w.id}
                  data-testid={`ws-option-${w.id}`}
                  onClick={() => setActiveId(w.id)}
                  className="cursor-pointer"
                >
                  <span className="truncate">{w.name}</span>
                  <span className="ml-auto text-xs font-mono text-muted-foreground uppercase">{w.plan}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="add-workspace-btn" onClick={addWorkspace} disabled={creating} className="cursor-pointer">
                <Plus size={16} className="mr-2" /> New workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {[...NAV, ...(user?.role === "admin" ? ADMIN_NAV : [])].map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Usage meter */}
        {active && (
          <div className="p-4 border-t border-border" data-testid="usage-meter">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase text-muted-foreground">Monthly usage</span>
              <span className="text-xs font-mono">{active.usage_count}/{active.usage_limit}</span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePct >= 90 ? "bg-destructive" : "bg-primary"}`}
                style={{ width: `${usagePct}%` }}
              />
            </div>
            {usagePct >= 80 && (
              <button
                onClick={() => navigate("/billing")}
                data-testid="upgrade-nudge"
                className="mt-3 w-full text-xs font-medium text-primary hover:underline"
              >
                Running low — upgrade plan →
              </button>
            )}
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 relative z-10">
        <header className="sticky top-0 z-20 glass bg-card/60 border-b border-border px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="md:hidden font-heading font-black text-lg">PostPilot</div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => navigate("/studio")}
              data-testid="topbar-new-repurpose"
              className="hidden sm:flex"
            >
              <MagicWand size={16} className="mr-1.5" weight="fill" /> New Repurpose
            </Button>
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="w-9 h-9 rounded-md border border-border flex items-center justify-center hover:bg-accent transition-colors"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-destructive">
                  <SignOut size={16} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 md:p-8 max-w-[1400px]"><PastDueBanner />{children}</main>
      </div>
    </div>
  );
}
